import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Cookie as CookieClient, createRealTimeCookie } from "@cookie-baker/browser";
import { createUseCookie } from "@cookie-baker/react";
import { isArray } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { checkIsExtension } from "../../api/helpers/helpers";
import { isRobot } from "../helpres/lib";
export const languageList = ["en", "es", "de", "it", "zh", "ja"];
export const localeLanguages = {
    en: "en-US",
    de: "de-DE",
    es: "es-ES",
    it: "it-IT",
    zh: "zh-CN",
    ja: "ja-JP",
};
export const LOCALE_COOKIE_KEY = process.env.REACT_APP_LOCALE_COOKIE_KEY || "helium10_app_language";
export const getLanguageAndRegion = (locale) => {
    const [language, region] = (locale || "").split("-");
    return { language, region };
};
export const cookie = new CookieClient();
const realTimeCookie = createRealTimeCookie(cookie);
const useCookie = createUseCookie(cookie, realTimeCookie);
export function TranslateProvider({ children, strings, TranslateContext, forceLang, }) {
    const isMount = useRef(false);
    const { language: helium10_app_language, region } = useMemo(() => {
        return getLanguageAndRegion(cookie.get()[LOCALE_COOKIE_KEY]);
    }, [LOCALE_COOKIE_KEY, cookie, getLanguageAndRegion]);
    const { language: cookieLanguage } = useCookie(({ [LOCALE_COOKIE_KEY]: locale }) => getLanguageAndRegion(locale));
    const [language, setLanguage] = useState(() => {
        if (!(!isRobot() && navigator.userAgent.includes("Chrome") && checkIsExtension())) {
            const lang = forceLang || helium10_app_language || "en";
            strings.setLanguage(lang);
            return lang;
        }
        else {
            return forceLang || strings.getLanguage();
        }
    });
    const changeLanguage = useCallback((language) => {
        strings.setLanguage(forceLang || language);
        setLanguage(forceLang || language);
    }, [forceLang, strings]);
    const setContent = useCallback((languages) => {
        strings.setContent(languages);
    }, [strings]);
    const getAvailableLanguages = useCallback(() => strings.getAvailableLanguages(), [strings]);
    useEffect(() => {
        if (!isRobot() && navigator.userAgent.includes("Chrome") && checkIsExtension()) {
            chrome.storage.sync.get("settings", ({ settings }) => {
                changeLanguage(forceLang || settings?.language || "en");
            });
        }
        else if (isMount.current) {
            if (helium10_app_language) {
                changeLanguage(forceLang || helium10_app_language);
            }
            else {
                // TODO - uncomment this after language switcher release
                // const native = navigator.language.split("-")[0] as SupportLanguagesType;
                // changeLanguage(languageList.includes(native) ? native : "en");
                changeLanguage(forceLang || "en");
            }
        }
        isMount.current = true;
    }, [forceLang, helium10_app_language, changeLanguage, region]);
    const t = useCallback((searchString, { nodes, ...params } = {}) => {
        if (params.count !== undefined && typeof params.count === "number") {
            const locale = strings.getLanguage();
            searchString += "." + new Intl.PluralRules(locale).select(params.count);
        }
        if (nodes && isArray(nodes)) {
            nodes.forEach((_, i) => {
                params[`node_${i}`] = "__REACT_NODE__";
            });
        }
        const stringResult = strings.getString(searchString) || strings.getString(searchString, "en");
        if (typeof stringResult === "string") {
            const result = strings.formatString(strings?.[stringResult] ? stringResult + "‎" : stringResult, params);
            if (nodes && isArray(nodes)) {
                return String(Array.isArray(result) ? result[0] : result)
                    .split("__REACT_NODE__")
                    .reduce((acc, el, i) => {
                    acc.push(el);
                    if (nodes[i]) {
                        acc.push(nodes[i]);
                    }
                    return acc;
                }, []);
            }
            return String(Array.isArray(result) ? result[0] : result);
        }
        else {
            return stringResult || searchString;
        }
    }, [strings, language, region]);
    const value = useMemo(() => ({
        region,
        language,
        cookieLanguage: forceLang || cookieLanguage || "en",
        getAvailableLanguages,
        changeLanguage,
        setContent,
        t,
    }), [language, forceLang, cookieLanguage, getAvailableLanguages, changeLanguage, setContent, t]);
    return _jsx(TranslateContext.Provider, { value: value, children: children });
}
